import { useCallback, useEffect, useRef, useState } from 'react'

// refer: https://github.com/facebook/docusaurus/blob/f22da5ac125cb128b8e1081969f82d4100cbc645/packages/docusaurus-theme-common/src/hooks/useHideableNavbar.ts

/**
 * Wires the imperative logic of a hideable navbar.
 * @param hideOnScroll If `false`, this hook is basically a no-op.
 */
export function useHideableNavbar(hideOnScroll = true): {
  /** A ref to the navbar component. Plug this into the actual element. */
  readonly navbarRef: (node: HTMLElement | null) => void
  /** If `false`, the navbar component should not be rendered. */
  readonly isNavbarVisible: boolean
  /** Call this before programmatic scrolls to prevent the navbar from hiding. */
  readonly disableHideOnScroll: () => void
} {
  const [isNavbarVisible, setIsNavbarVisible] = useState(hideOnScroll)
  const isFocusedAnchor = useRef(false)
  const navbarHeight = useRef(0)
  const navbarRef = useCallback((node: HTMLElement | null) => {
    if (node !== null) {
      navbarHeight.current = node.getBoundingClientRect().height
    }
  }, [])
  const lastPositionRef = useRef(0)

  useEffect(() => {
    const handleScroll = () => {
      const lastPosition = lastPositionRef.current

      if (!hideOnScroll) {
        return
      }

      // Needed mostly for handling rubber band scrolling.
      // See https://github.com/facebook/docusaurus/pull/5721
      if (window.scrollY < navbarHeight.current) {
        setIsNavbarVisible(true)
        return
      }

      if (isFocusedAnchor.current) {
        isFocusedAnchor.current = false
        return
      }

      const lastScrollTop = lastPosition
      const documentHeight =
        document.documentElement.scrollHeight - navbarHeight.current
      const windowHeight = window.innerHeight

      if (lastScrollTop && window.scrollY >= lastScrollTop) {
        setIsNavbarVisible(false)
      } else if (window.scrollY + windowHeight < documentHeight) {
        setIsNavbarVisible(true)
      }

      lastPositionRef.current = window.scrollY
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [hideOnScroll])

  const scrollLockTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lockScrollRef = useRef<(() => void) | null>(null)

  const cleanupScrollLock = useCallback(() => {
    if (scrollLockTimer.current) {
      clearTimeout(scrollLockTimer.current)
      scrollLockTimer.current = null
    }
    if (lockScrollRef.current) {
      window.removeEventListener('scroll', lockScrollRef.current)
      lockScrollRef.current = null
    }
  }, [])

  useEffect(() => cleanupScrollLock, [cleanupScrollLock])

  const disableHideOnScroll = useCallback(() => {
    isFocusedAnchor.current = true
    setIsNavbarVisible(true)
    cleanupScrollLock()
    const lockScroll = () => {
      isFocusedAnchor.current = true
    }
    lockScrollRef.current = lockScroll
    window.addEventListener('scroll', lockScroll, { passive: true })
    scrollLockTimer.current = setTimeout(() => {
      cleanupScrollLock()
      lastPositionRef.current = window.scrollY
    }, 1000)
  }, [cleanupScrollLock])

  return { navbarRef, isNavbarVisible, disableHideOnScroll }
}
